/**
 * Public property browse — `GET properties/public/`, the one unauthenticated
 * read endpoint (properties/routes.ts:214-239). `property_type`/`bedrooms`
 * accept the literal `'Any'` sentinel server-side, which is the same as
 * omitting the filter — this UI just omits it instead.
 *
 * `search`/`property_type` initialise from the URL's query string so a link
 * from LandingPage's hero search bar lands here pre-filtered; subsequent
 * filter changes stay in local state only (existing behaviour, unchanged).
 *
 * Renders the same elevated .featured-property-card treatment LandingPage.tsx
 * uses (not the old flat, bottom-border-divided .property-card list) — an
 * earlier revision deliberately kept this page's own dense-list look, but
 * that was superseded by explicit feedback that the two pages should read
 * consistently. `.property-card` itself still exists for
 * PublicPropertyDetailPage's "similar properties" section.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bath, BedDouble, Building2, DoorOpen, MapPin, Maximize, MessageCircle } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { listPublicProperties } from '../../api/endpoints/properties';
import { PROPERTY_TYPES, type PropertyType } from '../../types/property';
import {
  deriveBathroomRange,
  deriveBedroomRange,
  deriveFromPrice,
  deriveTotalSquareFeet,
  formatBathroomRange,
  formatBedroomRange,
  formatKes,
  isRecentlyListed,
} from '../../lib/propertyListing';
import { PropertyPicture } from '../../components/PropertyPicture';
import { ROUTES } from '../../routes/paths';

function readTypeParam(searchParams: URLSearchParams): PropertyType | '' {
  const value = searchParams.get('property_type');
  return value !== null && (PROPERTY_TYPES as readonly string[]).includes(value)
    ? (value as PropertyType)
    : '';
}

export function PublicPropertyBrowsePage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const [propertyType, setPropertyType] = useState<PropertyType | ''>(() =>
    readTypeParam(searchParams),
  );
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [bedrooms, setBedrooms] = useState('');

  const query = useQuery({
    queryKey: ['public-properties', { page, search, propertyType, minPrice, maxPrice, bedrooms }],
    queryFn: () =>
      listPublicProperties({
        page,
        search: search === '' ? undefined : search,
        property_type: propertyType === '' ? undefined : propertyType,
        min_price: minPrice === '' ? undefined : minPrice,
        max_price: maxPrice === '' ? undefined : maxPrice,
        bedrooms: bedrooms === '' ? undefined : bedrooms,
      }),
  });

  const properties = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;

  const mapViewParams = new URLSearchParams();
  if (search !== '') mapViewParams.set('search', search);
  if (propertyType !== '') mapViewParams.set('property_type', propertyType);
  if (minPrice !== '') mapViewParams.set('min_price', minPrice);
  if (maxPrice !== '') mapViewParams.set('max_price', maxPrice);
  if (bedrooms !== '') mapViewParams.set('bedrooms', bedrooms);
  const mapViewQuery = mapViewParams.toString();

  return (
    <section>
      <h1>Browse properties</h1>

      <p>
        <Link
          to={mapViewQuery === '' ? ROUTES.publicPropertiesMap : `${ROUTES.publicPropertiesMap}?${mapViewQuery}`}
          className="button button--outlined"
        >
          <MapPin size={16} aria-hidden="true" style={{ marginRight: 'var(--space-xs)', verticalAlign: '-2px' }} />
          Map view
        </Link>
      </p>

      <form
        className="filter-bar"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
        }}
      >
        <div className="filter-bar__field">
          <label htmlFor="browse-search">Search</label>
          <input
            id="browse-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="filter-bar__field">
          <label htmlFor="browse-type">Type</label>
          <select
            id="browse-type"
            value={propertyType}
            onChange={(event) => setPropertyType(event.target.value as PropertyType | '')}
          >
            <option value="">Any</option>
            {PROPERTY_TYPES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-bar__field">
          <label htmlFor="browse-min-price">Min rent</label>
          <input
            id="browse-min-price"
            type="number"
            value={minPrice}
            onChange={(event) => setMinPrice(event.target.value)}
          />
        </div>

        <div className="filter-bar__field">
          <label htmlFor="browse-max-price">Max rent</label>
          <input
            id="browse-max-price"
            type="number"
            value={maxPrice}
            onChange={(event) => setMaxPrice(event.target.value)}
          />
        </div>

        <div className="filter-bar__field">
          <label htmlFor="browse-bedrooms">Bedrooms</label>
          <input
            id="browse-bedrooms"
            type="number"
            value={bedrooms}
            onChange={(event) => setBedrooms(event.target.value)}
          />
        </div>

        <button type="submit">Search</button>
      </form>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}
      {query.isSuccess && properties.length === 0 && <p>No properties match your search.</p>}

      {query.isSuccess && properties.length > 0 && (
        <ul className="featured-properties">
          {properties.map((property) => {
            const fromPrice = deriveFromPrice(property.units);
            const bedroomRange = deriveBedroomRange(property.units);
            const bathroomRange = deriveBathroomRange(property.units);
            const totalSquareFeet = deriveTotalSquareFeet(property.units);
            return (
              <li key={property.id} className="featured-property-card">
                {property.primary_image?.image != null ? (
                  <PropertyPicture
                    className="featured-property-card__thumb"
                    src={property.primary_image.image}
                    alt=""
                    size="medium"
                  />
                ) : (
                  <span
                    className="featured-property-card__thumb thumb--placeholder"
                    aria-hidden="true"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    No photo
                  </span>
                )}
                <div className="featured-property-card__body">
                  <div className="featured-property-card__heading">
                    <h3>
                      <Link to={String(property.id)}>{property.name}</Link>
                    </h3>
                    {property.whatsapp_number !== '' && (
                      <a
                        className="property-card__whatsapp"
                        href={`https://wa.me/${property.whatsapp_number.replace(/\D/g, '')}?text=${encodeURIComponent(
                          `Hi, I'm interested in ${property.name} (${property.address}). Is it still available?`,
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Message ${property.name} on WhatsApp`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <MessageCircle size={18} aria-hidden="true" />
                      </a>
                    )}
                  </div>

                  <p className="featured-property-card__location">
                    <MapPin size={14} aria-hidden="true" />
                    {property.address}, {property.city}, {property.state}
                  </p>

                  {property.description !== '' && (
                    <p className="featured-property-card__description">{property.description}</p>
                  )}

                  <ul className="spec-row">
                    <li className="spec-row__item">
                      <Building2 size={16} aria-hidden="true" />
                      <span>{property.property_type}</span>
                    </li>
                    {bedroomRange !== null && (
                      <li className="spec-row__item">
                        <BedDouble size={16} aria-hidden="true" />
                        <span>{formatBedroomRange(bedroomRange)}</span>
                      </li>
                    )}
                    {bathroomRange !== null && (
                      <li className="spec-row__item">
                        <Bath size={16} aria-hidden="true" />
                        <span>{formatBathroomRange(bathroomRange)}</span>
                      </li>
                    )}
                    {totalSquareFeet !== null && (
                      <li className="spec-row__item">
                        <Maximize size={16} aria-hidden="true" />
                        <span>{totalSquareFeet.toLocaleString('en-KE')} sq ft</span>
                      </li>
                    )}
                    <li className="spec-row__item">
                      <DoorOpen size={16} aria-hidden="true" />
                      <span>
                        {property.vacant_units_count} / {property.total_units} vacant
                      </span>
                    </li>
                  </ul>

                  {property.features.length > 0 && (
                    <ul className="chip-row">
                      {property.features.slice(0, 5).map((feature) => (
                        <li key={feature} className="chip">
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="featured-property-card__footer">
                    <p className="badge-row">
                      {property.vacant_units_count > 0 && (
                        <span className="badge badge--available">Vacant units available</span>
                      )}
                      {isRecentlyListed(property.created_at) && (
                        <span className="badge badge--new">New</span>
                      )}
                    </p>
                    {fromPrice !== null ? (
                      <p className="listing-price">From {formatKes(fromPrice)}</p>
                    ) : property.vacant_units_count === 0 ? (
                      <p className="listing-price listing-price--muted">Fully occupied</p>
                    ) : (
                      <span />
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>
    </section>
  );
}

export default PublicPropertyBrowsePage;
